import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { DailyReportService } from '../../../../daily-report.service';
import { takeUntil } from 'rxjs/operators';
import { weatherConditions, weatherFields } from '../../../../../../../../../../shared/core/shared.selectors';
import { Ability, subject } from '@casl/ability';
import { dailyReport } from '../../../../../../../../core/projects.selectors';

@Component({
    selector: 'app-weather-details-form',
    templateUrl: './weather-details-form.component.html',
    styleUrls: ['./weather-details-form.component.scss'],
    standalone: false
})
export class WeatherDetailsFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any;
  skyConditionOptions = [];

  form = new FormGroup<any>({
    avgtemp: new FormControl({ value: null, disabled: false }, {
      validators: [Validators.required],
    }),
    maxtemp: new FormControl({ value: null, disabled: false }, {
      validators: [Validators.required],
    }),
    mintemp: new FormControl({ value: null, disabled: false }, {
      validators: [Validators.required],
    }),
    skyCondition: new FormControl({ value: null, disabled: false }),
    wind: new FormControl({ value: null, disabled: false }),
    note: new FormControl({ value: null, disabled: false }),
  });
  formSubscription: Subscription = new Subscription();
  dailyReport: any;
  editable: any;
  weatherFields: any = [];
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private projectsService: ProjectsService,
    private dailyReportService: DailyReportService,
    private ability: Ability,
  ) {

    this.store.select(weatherConditions)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.skyConditionOptions = data;
      });

  }

  ngOnChanges(changes: SimpleChanges) {
    this.formSubscription = this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.editable) {
          this.projectsService.isAnyFormDirty.next(true);
        }
      });
  }

  ngOnInit() {
    this.store.select(weatherFields)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((wf: any) => {
        this.weatherFields = wf || [];

        this.weatherFields?.forEach(o => {
          if (!this.form.controls[o?.name]) {
            this.form.addControl(
              o?.name,
              new FormControl({ value: '', disabled: false }),
            );
          }
        });

        // Patch dynamic weather field values from field_values array or top-level data keys
        if (this.data) {
          const valuesToPatch: any = {};

          this.weatherFields.forEach(field => {
            // First try field_values array (by matching field id)
            const fv = this.data?.field_values?.find(v => v.id === field.id);
            if (fv !== undefined) {
              valuesToPatch[field.name] = fv.value;
            } else if (this.data[field.name] !== undefined) {
              // Fallback: exact key match
              valuesToPatch[field.name] = this.data[field.name];
            } else {
              // Fallback: find any key that starts with the field name (e.g. rainfall_amount_inches -> rainfall_amount)
              const matchingKey = Object.keys(this.data).find(k => k.startsWith(field.name));
              if (matchingKey) {
                valuesToPatch[field.name] = this.data[matchingKey];
              }
            }
          });

          if (Object.keys(valuesToPatch).length) {
            this.form.patchValue(valuesToPatch, { emitEvent: false });
          }
        }
      });

    if (this.data) {
      this.dailyReportService.disableAddMenu.next(false);
      const valuesToPatch: any = {};

      for (const key in this.form.controls) {
        if (key === 'skyCondition' && this.data?.condition?.code !== undefined) {
          valuesToPatch[key] = this.data.condition.code;
        } else if (this.data?.hasOwnProperty(key)) {
          valuesToPatch[key] = this.data[key];
        }
      }

      this.form.patchValue(valuesToPatch, { emitEvent: false });
    }

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.save();
      });

    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.form) {
          this.delete();
        }
      });

    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.dailyReport = { ...report };
        this.editable = this.ability.can('create', subject('DailyReport', this.dailyReport));
        if (!this.editable) {
          this.form.disable({ emitEvent: false });
        } else {
          this.form.enable({ emitEvent: false });
        }
      });
  }

  save() {
    let values = this.form.getRawValue();
    const { condition, ...restData } = this.data;
    const { skyCondition, ...rest } = values;
    const tempData = this.skyConditionOptions.find((o) => o.code === skyCondition);
    values = {
      ...rest,
      skyCondition: tempData,
    };

    let payload = {
      ...restData,
      condition: tempData,
      ...values,
    };

    const fieldValues = this.weatherFields
      .map(field => {
        const val = payload[field.name];
        return val !== undefined
          ? { id: field.id, value: Number(val) }
          : null;
      })
      .filter(Boolean);

    payload = {
      ...payload,
      field_values: fieldValues,
    };

    this.dailyReportService.save.next({
      type: 'weather',
      data: payload,
    });
  }

  delete() {
    this.dailyReportService.delete.next({
      type: 'weather',
      id: this.data?.id,
    });
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
